import type { Subject } from "@subjects/subjectsClient";
import { seedSubject } from "@subjects/subjectsQueries";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

import { resolveSelection, type WorkspaceSearch } from "./selection";

/**
 * URL-backed workspace selection, read side. The route search params are the single
 * source of truth for which room/visit/subject/view is being viewed; this hook reads
 * them and resolves the one active selection via `resolveSelection`. See
 * `useWorkspaceNavigation` for the writes.
 *
 * Callers off the `/` route (admin panels that only need the navigations) have no
 * selection to read, so it falls back to an empty selection rather than throwing.
 */
export const useWorkspaceSelection = () => {
  const search = useSearch({ from: "/", shouldThrow: false }) ?? {};
  return {
    selectedRoomUuid: search.room ?? null,
    selectedVisitUuid: search.visit ?? null,
    viewedSubjectId: search.subject ?? null,
    viewedViewId: search.view ?? null,
    selection: resolveSelection(search),
  };
};

/**
 * URL-backed workspace selection, write side. Every action below is a single
 * atomic `navigate` call - no store writes, no `replace: true` - so the URL is
 * the only place this state lives and back/forward always lands on a
 * consistent selection.
 */
export const useWorkspaceNavigation = () => {
  const navigate = useNavigate();

  // Selecting a room clears the overlays (subject + view), and clears the
  // visit unless the same room is re-selected - re-selecting the room you're
  // already in shouldn't reset the visit you're looking at inside it, so the
  // whole selection is left alone (a true no-op, not just an unclearing).
  const selectRoom = useCallback(
    (uuid: string | null) =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => {
          if (prev.room === uuid) return prev;
          return {
            ...prev,
            room: uuid ?? undefined,
            visit: undefined,
            subject: undefined,
            view: undefined,
          };
        },
      }),
    [navigate],
  );

  // Show a room outright, in one atomic navigation. Unlike selectRoom, whose
  // same-room no-op deliberately preserves an in-room visit selection, this
  // always clears the visit and both overlays so the room view wins, even when
  // the URL arrived with a visit selected in another room.
  const showRoom = useCallback(
    (uuid: string) =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          room: uuid,
          visit: undefined,
          subject: undefined,
          view: undefined,
        }),
      }),
    [navigate],
  );

  // Selecting a visit card clears the overlays and leaves the room alone.
  const selectVisitCard = useCallback(
    (uuid: string | null) =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          visit: uuid ?? undefined,
          subject: undefined,
          view: undefined,
        }),
      }),
    [navigate],
  );

  // Select a patient and align the room in one atomic navigation. Used when
  // jumping to a patient from outside the rail (e.g. a notification): pass the
  // patient's current room (or null) so room and visit can't disagree on
  // landing.
  const selectVisit = useCallback(
    (visitUuid: string, roomUuid: string | null) =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          room: roomUuid ?? undefined,
          visit: visitUuid,
          subject: undefined,
          view: undefined,
        }),
      }),
    [navigate],
  );

  // Opening a subject is a full navigation, not an overlay over the room/visit:
  // it clears room, visit, and view together. Closing a subject exits to the
  // rooms grid (see `closeSubject`), so no forward user action ever reveals a
  // room/visit "underneath" it - carrying that pair along would be dead state
  // that only resurfaces as a confusing fallback when the subject 404s on a
  // deep link. Back/forward still returns to the previous room/visit because
  // history holds the old URL. The caller already holds the full record
  // (search modal, calendar), so seed the cache before
  // navigating - the subject pane then reads a warm cache instead of flashing
  // a fetch, and only hits the network on a refresh or a deep link.
  const openSubject = useCallback(
    (subject: Subject) => {
      seedSubject(subject);
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          room: undefined,
          visit: undefined,
          subject: subject.id,
          view: undefined,
        }),
      });
    },
    [navigate],
  );

  // Closing a subject exits to the rooms grid outright. `openSubject` already
  // clears room/visit, so there's nothing selected underneath to return to;
  // this clears all four keys anyway so it doubles as the single "exit" every
  // pane in WorkspaceContent shares (rooms and visits included), landing on a
  // clean rooms grid rather than back on whatever preceded the pane.
  const closeSubject = useCallback(
    () =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          room: undefined,
          visit: undefined,
          subject: undefined,
          view: undefined,
        }),
      }),
    [navigate],
  );

  // A viewed SavedView is a top-level overlay like a subject: it wins in the
  // workspace content and clears the subject underneath; closing it (null)
  // drops just the view, returning to whatever room/visit was selected.
  const setViewedView = useCallback(
    (viewId: string | null) =>
      void navigate({
        to: "/",
        search: (prev: WorkspaceSearch) => ({
          ...prev,
          subject: undefined,
          view: viewId ?? undefined,
        }),
      }),
    [navigate],
  );

  // The header's home button: back to a clean slate, no room, visit, subject,
  // or view.
  const goHome = useCallback(
    () => void navigate({ to: "/", search: {} }),
    [navigate],
  );

  return {
    selectRoom,
    showRoom,
    selectVisitCard,
    selectVisit,
    openSubject,
    closeSubject,
    setViewedView,
    goHome,
  };
};
